import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ArrowRight } from 'lucide-react';
import { Toast } from 'react-vant';
import api from '../api';
import { useAuthStore } from '../store/useStore';

export default function LoginPage() {
  const navigate = useNavigate();
  const authLogin = useAuthStore((state) => state.login);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeText, setCodeText] = useState('获取验证码');
  const [codeDisabled, setCodeDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const phoneRegex = /^1\d{10}$/;
  const phoneValid = phoneRegex.test(phone);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('login_expired') === '1') {
      sessionStorage.removeItem('login_expired');
      Toast.info('登录已过期，请重新登录');
    }
  }, []);

  const handleGetCode = async () => {
    setError('');
    if (!phoneValid) {
      Toast.info('请输入正确的手机号');
      return;
    }

    setCodeDisabled(true);
    setCodeText('发送中...');

    try {
      const res = await api.login.sendSms({
        mobile: phone,
        type: 'login',
      });

      const countdownSeconds = res?.time && res.time > 0 ? res.time : 60;
      if (res?.message) {
        Toast.info(res.message);
      }

      let countdown = countdownSeconds;
      setCodeText(`${countdown} 秒后重发`);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      timerRef.current = setInterval(() => {
        countdown -= 1;
        if (countdown > 0) {
          setCodeText(`${countdown} 秒后重发`);
        } else {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setCodeText('获取验证码');
          setCodeDisabled(false);
        }
      }, 1000);
    } catch (e: unknown) {
      console.error('sendMobileCode failed:', e);
      Toast.info('验证码发送失败');
      setCodeText('获取验证码');
      setCodeDisabled(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!phone || !code) {
      Toast.info('请完善手机号和验证码');
      return;
    }

    setLoading(true);
    try {
      const res = await api.login.loginByCode({
        mobile: phone,
        verCode: code,
      });

      const token = res?.token || '';
      if (!token) {
        Toast.info('登录成功，但未获取到 token');
        return;
      }

      localStorage.setItem('auth_token', token);
      authLogin(phone, '');

      const userId = res?.userInfo?.userId;

      if (!userId || String(userId).trim() === '') {
        localStorage.removeItem('user_id');
      } else {
        localStorage.setItem('user_id', String(userId));
      }

      navigate('/');
    } catch (err: unknown) {
      console.error('登录异常:', err);
      let errorMsg = '登录失败，请重试';
      if (err instanceof Error && err.message) {
        errorMsg = err.message;
      } else if (typeof err === 'object' && err !== null) {
        const maybeError = err as {
          response?: { data?: { message?: string; retDesc?: string } };
          request?: unknown;
        };
        if (maybeError.response) {
          errorMsg = maybeError.response.data?.message || maybeError.response.data?.retDesc || errorMsg;
        } else if (maybeError.request) {
          errorMsg = '网络连接失败，请检查网络';
        }
      }
      setError(errorMsg);
      Toast.info(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: branding */}
      <div className="hidden lg:flex lg:w-[500px] gradient-brand relative overflow-hidden items-center justify-center p-12">
        {/* Decorative elements - refined geometric shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-12 w-80 h-80 border border-white/[0.06] rounded-full" />
          <div className="absolute bottom-20 right-8 w-[460px] h-[460px] border border-white/[0.04] rounded-full" />
          <div className="absolute top-1/3 left-1/4 w-60 h-60 border border-white/[0.05] rounded-full" />
          <div className="absolute top-24 right-28 w-36 h-36 bg-white/[0.02] rounded-3xl rotate-[35deg]" />
          <div className="absolute bottom-28 left-20 w-28 h-28 bg-white/[0.03] rounded-2xl -rotate-[12deg]" />
          {/* Subtle gradient orb */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-white/[0.03] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 text-white max-w-sm">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-white/[0.12] rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/[0.15] overflow-hidden">
              <img src="/assets/logo.png" alt="ODance" className="w-full h-full object-cover" />
            </div>
            <span className="text-[28px] font-bold tracking-tight">Oakay Studio</span>
          </div>
          <h1 className="text-[34px] font-bold mb-5 leading-[1.15] tracking-tight">
            让机器人跳起<br />
            <span className="text-white/70 font-medium">属于你的舞蹈</span>
          </h1>
          <p className="text-white/40 text-sm leading-relaxed">
            专业机器人运控平台，支持仿真视频生成、关键帧编辑、舞蹈模型训练，一站式完成从创意到执行的全流程。
          </p>
          {/* Feature pills */}
          <div className="flex flex-wrap gap-2 mt-8">
            {['仿真视频生成', '关键帧编辑', '模型训练', '一键部署'].map(tag => (
              <span key={tag} className="px-3 py-1.5 bg-white/[0.08] backdrop-blur-sm rounded-full text-[11px] text-white/60 border border-white/[0.08]">
                {tag}
              </span>
            ))}
          </div>
        </div>    
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-bg-main">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="lg:hidden flex items-center gap-3 mb-10 justify-center">
            <div className="w-10 h-10 gradient-brand rounded-xl flex items-center justify-center overflow-hidden">
              <img src="/assets/logo.png" alt="ODance" className="w-full h-full object-cover" />
            </div>
            <span className="text-2xl font-bold text-text-primary">ODance</span>
          </div>

          <h2 className="text-[22px] font-bold text-text-primary mb-1 tracking-tight">
            登录
          </h2>
          <p className="text-sm text-text-secondary mb-8">
            输入手机号与验证码后即可登录
          </p>

          {error && (
            <div className="mb-5 px-4 py-3 bg-danger-50 rounded-xl border border-danger/10 text-sm text-danger-dark flex items-center gap-2 animate-slide-up">
              <div className="w-1.5 h-1.5 bg-danger rounded-full shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">手机号</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="请输入手机号"
                  className="w-full pl-10 pr-4 py-3 bg-white border border-border-main rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all shadow-soft" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">验证码</label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <input type="text" value={code} onChange={e => setCode(e.target.value)} placeholder="请输入验证码"
                    className="w-full px-4 py-3 bg-white border border-border-main rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all shadow-soft" />
                </div>
                <button
                  type="button"
                  onClick={handleGetCode}
                  disabled={codeDisabled || !phoneValid}
                  className="shrink-0 px-4 py-3 bg-white border border-border-main rounded-xl text-sm font-semibold text-text-primary disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-soft"
                >
                  {codeText}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift shadow-sm shadow-brand/25 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all mt-6">
              {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : '确定'}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
